import { PhoneSpecs } from '../phones'
import { Answers, ScenarioType, Steps } from '../state/interfaces'
import { getPathname, generateQueryUrlFragment } from './endpointElementsGenerator'
import { getLocale } from '../i18n/locale'
import { getCookie, setCookie } from '../scenarios'
import { formatPostalCode, mapKeySearchCriterias, mapProvince } from '../scenarios/utils'
import { sendGA4Event } from '../analytics/ga4'

const DEFAULT_PROVINCE = 'quebec'

export const getPhoneList = async (province: string): Promise<Array<PhoneSpecs>> => {
  const endpoint = `${process.env.REACT_APP_PHONES_API_URL}/${province}`
  const response = await fetch(endpoint)
  return (await response.json()).data
}

export const findAvailableDownloadSpeeds = async (provider: string): Promise<Array<number>> => {
  const endpoint = `${process.env.REACT_APP_SCENARIO_OUTPUT_URL}/api/download-speed/internet/${provider}`
  const response = await fetch(endpoint)
  return (await response.json()).speeds
}

export interface CreateQuestionnaireRequest {
  answers: Answers
  scenarioType: string
  scenarioAdditionalInformation: Record<string, unknown>
  referrerUrl: string
}
export interface CreateQuestionnaireResponse {
  phoneModelId?: number
  redirectUrl: string
}

export const createQuestionnaire = async ({
  answers,
  scenarioType,
  scenarioAdditionalInformation,
}: CreateQuestionnaireRequest): Promise<CreateQuestionnaireResponse> => {
  const scenariosWithNoResponse = [
    ScenarioType.MOBILE_REVIEWS,
    ScenarioType.INTERNET_REVIEWS,
    ScenarioType.LEAD_MOBILE,
    ScenarioType.LEAD_INTERNET,
    ScenarioType.NEWSLETTER,
    ScenarioType.FIBER_OPTIC_ALERT,
    ScenarioType.TICKET,
  ]

  if (scenariosWithNoResponse.includes(scenarioType as ScenarioType)) {
    return {
      redirectUrl: '',
    }
  }

  const baseEndpoint = `${process.env.REACT_APP_SCENARIO_OUTPUT_URL}`
  const pathname = getPathname(scenarioType)
  const queryUrlFragment = generateQueryUrlFragment(scenarioType, answers, scenarioAdditionalInformation)
  const province: string =
    getCookie(Steps.PROVINCE) || (answers[Steps.PROVINCE] ? answers[Steps.PROVINCE]![0] : DEFAULT_PROVINCE)

  if (scenarioType === ScenarioType.ADVISE_INTERNET) {
    return {
      redirectUrl: `${baseEndpoint}/${getLocale()}/${pathname}${queryUrlFragment}`,
    }
  }

  return {
    redirectUrl: `${baseEndpoint}${getLocale() === 'fr' ? '/fr' : ''}/${province}/${pathname}${queryUrlFragment}`,
  }
}

const createReviewsBodyRequest = (answers: Answers, scenarioType: ScenarioType): Record<string, unknown> => {
  const response: Record<string, string | number | null> = {
    address: answers['address-location']![0] || null,
    country: getCookie('internetSearchCountry') || getCookie('country') || null,
    customerServiceRating: answers['bill']![0] || 0,
    email: answers['email-address']![0] || null,
    ip: getCookie('ip') || null,
    isp: getCookie('isp') || null,
    locale: getLocale() || null,
    locality: getCookie('internetSearchLocality') || getCookie('city') || 'nc',
    'name-nickname': answers['name-nickname']![0] || null,
    placeId: getCookie('placeId') || null,
    postalcode: getCookie('internetSearchPostalcode') || 'nc',
    problemSolvingRating: answers['customer-service']![0] || 0,
    province: getCookie('internetSearchProvince') || getCookie('province') || DEFAULT_PROVINCE,
    provinceReview: getCookie('internetSearchProvince') || getCookie('province') || DEFAULT_PROVINCE,
    qualityServicesRating: answers['onboarding-experience']![0] || 0,
    recommended: answers['recommend-provider']![0] ? (answers['recommend-provider']![0] === 'yes' ? 1 : 0) : 0, // Not recommended par default (must not normally get there)
    screenResolution: `${window.screen.height} x ${window.screen.width}`,
    speedEfficiencyRating: answers['digital-experience']![0] || 0,
    streetName: getCookie('internetSearchStreetName') || null,
    streetNumber: getCookie('internetSearchStreetNumber') || null,
  }

  if (scenarioType === ScenarioType.MOBILE_REVIEWS) {
    response.networkQualityRating = answers['network']![0] || 0
    response.providerMobile = getCookie('providerSlugReviews')
    if (!response.providerMobile) {
      response.providerMobile = answers['select-current-provider']![0] || null
    }
  } else if (scenarioType === ScenarioType.INTERNET_REVIEWS) {
    response.internetQualityRating = answers['network']![0] || 0
    response.providerInternet = getCookie('providerSlugReviews')
    if (!response.providerInternet) {
      response.providerInternet = answers['select-current-provider']![0] || null
    }
  }

  if (answers['final-word']) {
    response.title = answers['final-word']![0] || null
    response.comment = answers['final-word']![1] || null
  } else if (answers['final-word-referral-code']) {
    response.title = answers['final-word-referral-code']![0] || null
    response.comment = answers['final-word-referral-code']![1] || null
    response.referralCode = answers['final-word-referral-code']![2] || null
  }

  if (answers['confirm-review']) {
    if (answers['confirm-review'].includes('confirm-newsletters')) {
      response['confirm-newsletters'] = 'yes'
    } else {
      response['confirm-newsletters'] = 'no'
    }
  }

  return response
}

const createAlertBodyRequest = (answers: Answers, scenarioType: ScenarioType): Record<string, unknown> => {
  const response: Record<string, unknown> = {}

  for (const answer in answers) {
    let key = answer as Steps
    // to unify postal codes
    if (answer === Steps.POSTAL_CODE_INTERNET) {
      key = 'postal-code' as Steps
    }

    if (answer === Steps.INTERNET_SPEED) {
      key = 'download-speed' as Steps
    }

    response[key] = answers[answer as Steps]!.join(';')
  }

  // TODO: Find a way to make this more robust. Ex: Answers...[3] could change its value someday
  if (answers['address-location']) {
    response['locality'] = getCookie('internetSearchLocality') || answers['address-location']![3] || null
    response['country'] = getCookie('internetSearchCountry') || answers['address-location']![5] || null
    response['street-name'] = getCookie('internetSearchStreetName') || answers['address-location']![2] || null
    response['postal-code'] =
      getCookie('internetSearchPostalcode') || formatPostalCode(answers['address-location']![6]) || null
    response['street-number'] = getCookie('internetSearchStreetNumber') || answers['address-location']![1] || null
    response['province'] =
      getCookie('internetSearchProvince') || mapProvince(answers['address-location']![4]) || DEFAULT_PROVINCE
  } else {
    response['province'] = getCookie('province') || DEFAULT_PROVINCE
  }
  response['locale'] = getLocale()
  response['url'] = getCookie('referrerUrl') || document.referrer || window.location.href

  if (answers['confirm-alert']) {
    if (answers['confirm-alert'].includes('confirm-newsletters')) {
      response['confirm-newsletters'] = 'yes'
    } else {
      response['confirm-newsletters'] = 'no'
    }
  }

  if (scenarioType === ScenarioType.INTERNET || scenarioType === ScenarioType.INTERNET_ALERT) {
    response['monthly-usage'] = answers['monthly-usage']
      ? answers['monthly-usage'][0]
        ? answers['monthly-usage'][0]
        : 0
      : 0
    response['current-provider'] = getCookie('currentInternetProvider') ? getCookie('currentInternetProvider') : 'nc'
  }

  if (scenarioType === ScenarioType.MOBILE_ALERT_OBSERVER) {
    response['how-many-calling-minutes'] = 'unlimitedcalls'
  }

  return response
}

const createLeadBodyRequest = (answers: Answers, scenarioType: ScenarioType): Record<string, unknown> => {
  console.log(scenarioType)
  const response: Record<string, unknown> = {}
  const excludedAnswers = [Steps.PROVIDER_AVAILABILITY, Steps.START_LEAD]

  for (const answer in answers) {
    if (!excludedAnswers.includes(answer as Steps)) {
      response[answer] = answers[answer as Steps]![0]
    }
  }

  response['locale'] = getLocale()
  response['isDeal'] = answers['provider-availability']![1]
  response['email'] = answers['email-address-lead']![0]
  response['firstName'] = answers['first-name']![0]
  response['lastName'] = answers['last-name']![0]
  response['phone'] = answers['phone-number']![0]
  response['address'] = answers['address-location']![0]
  // response['locality'] = getCookie('internetSearchLocality') || answers['address-location']![3] || null
  // response['country'] = getCookie('internetSearchCountry') || answers['address-location']![5] || null
  // response['street-name'] = getCookie('internetSearchStreetName') || answers['address-location']![2] || null
  response['postalCode'] =
    getCookie('internetSearchPostalcode') || formatPostalCode(answers['address-location']![6]) || null
  // response['street-number'] = getCookie('internetSearchStreetNumber') || answers['address-location']![1] || null
  response['province'] =
    getCookie('internetSearchProvince') || mapProvince(answers['address-location']![4]) || DEFAULT_PROVINCE
  response['placeId'] = getCookie('placeId') || null

  if (getCookie('scenario') && JSON.parse(getCookie('scenario')).initialInformation) {
    const additionalInformation: Record<string, unknown> = JSON.parse(
      getCookie('scenario').replace(/%3B/g, ';'),
    ).initialInformation
    for (const information in additionalInformation) {
      if (typeof additionalInformation[information] === 'object') {
        const informationAsString = JSON.stringify(additionalInformation[information])
        response[information] = informationAsString
      } else {
        response[information] = additionalInformation[information] as string
        if (information === 'providerSlug') {
          response['provider'] = additionalInformation[information] as string
        }
      }
    }
  }
  return response
}

const createTicketBodyRequest = (answers: Answers, scenarioType: ScenarioType): Record<string, unknown> => {
  console.log(scenarioType)
  const response: Record<string, unknown> = {}
  response['locale'] = getLocale()
  response['formComments'] = answers['comment-ticket']![0]
  response['formEmail'] = answers['email-address-ticket']![0]
  response['formIssue'] = answers['ticket-initial-info']![0]
  response['formName'] = `${answers['first-name']![0]} ${answers['last-name']![0]}`
  response['urlPart'] = getCookie('referrerUrl') || document.referrer || window.location.href
  response['url'] = getCookie('referrerUrl') || document.referrer || window.location.href
  if (answers['comment-ticket']) {
    if (answers['comment-ticket'].includes('confirm-newsletters')) {
      response['confirm-newsletters'] = 'yes'
    } else {
      response['confirm-newsletters'] = 'no'
    }
  }

  if (getCookie('scenario') && JSON.parse(getCookie('scenario')).initialInformation) {
    const additionalInformation: Record<string, unknown> = JSON.parse(
      getCookie('scenario').replace(/%3B/g, ';'),
    ).initialInformation
    for (const information in additionalInformation) {
      if (typeof additionalInformation[information] === 'object') {
        const informationAsString = JSON.stringify(additionalInformation[information])
        response[information] = informationAsString
      } else {
        response[information] = additionalInformation[information] as string
        if (information === 'providerSlug') {
          response['provider'] = additionalInformation[information] as string
        }
      }
    }
  }
  return response
}

export const sendReviewsRequest = async (answers: Answers, scenarioType: Partial<ScenarioType>) => {
  const mapScenarioTypeAndReviewsEndpoint: Partial<Record<Partial<ScenarioType>, string>> = {
    [ScenarioType.MOBILE_REVIEWS]: 'createMobileProviderReview',
    [ScenarioType.INTERNET_REVIEWS]: 'createInternetProviderReview',
  }

  const payload = new FormData()
  const payloadRawObject = createReviewsBodyRequest(answers, scenarioType)
  for (const i in payloadRawObject) {
    payload.append(i, payloadRawObject[i] as string)
  }

  const endpoint = `${process.env.REACT_APP_SCENARIO_OUTPUT_URL}/${getLocale()}/${
    mapScenarioTypeAndReviewsEndpoint[scenarioType as Partial<ScenarioType>]
  }`

  await fetch(endpoint, {
    method: 'POST',
    body: payload,
    mode: 'no-cors',
    headers: {
      'Content-type': 'application/json',
    },
  })

  setTimeout(() => {
    identifyCioUser(answers)
  }, 2000)
}

export const sendNewTicketRequest = async (answers: Answers, scenarioType: Partial<ScenarioType>) => {
  const payloadRawObject = createTicketBodyRequest(answers, scenarioType)
  const endpoint = `${process.env.REACT_APP_SCENARIO_OUTPUT_URL}/dyn/${getLocale()}/createIssue`

  await fetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(payloadRawObject),
    headers: {
      'Content-type': 'application/json',
    },
  })

  setTimeout(() => {
    identifyCioUser(answers)
  }, 2000)
}

export const identifyCioUser = async (answers: Answers) => {
  const email = getEmailFromAnswers(answers)

  const cioPhId = getCookie('cio_ph_id')
  const cioCookieParameter = cioPhId ? `&id=${cioPhId}` : ''

  const response = await fetch(
    `${process.env.REACT_APP_SCENARIO_OUTPUT_URL}/api/customer/identify?dyn=1&email=${email}${cioCookieParameter}`,
  )

  const jsonResponse = await response.json()

  const { cookies } = jsonResponse

  if (cookies) {
    cookies.forEach((cookie: { name: string; value: string }) => {
      if (cookie.name) setCookie(cookie.name, cookie.value, 365)
    })
  }
}

export const sendNewsletterRequest = async (answers: Answers, scenarioType: Partial<ScenarioType>) => {
  console.log(scenarioType)
  const type = 'general'
  const email = answers['email-address']![0]
  const locale = getLocale()

  const endpoint = `${process.env.REACT_APP_SCENARIO_OUTPUT_URL}/api/register-to-newsletter/${type}/${email}?locale=${locale}`

  await fetch(endpoint)

  setTimeout(() => {
    identifyCioUser(answers)
  }, 2000)
}

export const sendAlertRequest = async (answers: Answers, scenarioType: Partial<ScenarioType>) => {
  const mapScenarioTypeAndAlertEndpointFragment: Partial<Record<Partial<ScenarioType>, string>> = {
    [ScenarioType.MOBILE]: 'mobile',
    [ScenarioType.MOBILE_ALERT]: 'mobile',
    [ScenarioType.MOBILE_ALERT_OBSERVER]: 'mobile',
    [ScenarioType.MOBILE_SEARCH_EMAIL]: 'mobile',
    [ScenarioType.INTERNET_ALERT]: 'internet',
    [ScenarioType.INTERNET_ALERT_OBSERVER]: 'internet',
  }

  // const emailAddress = answers['email-address']![0] || null
  // const segmentRegisterEndpoint = `${process.env.REACT_APP_SCENARIO_OUTPUT_URL}/api/register-to-newsletter/${
  //   mapScenarioTypeAndAlertEndpointFragment[scenarioType as Partial<ScenarioType>]
  // }/${emailAddress}`;

  let alertEndpoint: RequestInfo | URL = ''

  const payload = new FormData()
  const payloadRawObject = createAlertBodyRequest(answers, scenarioType)
  for (const i in payloadRawObject) {
    payload.append(i, payloadRawObject[i] as string)
  }

  if (scenarioType === ScenarioType.COMPETITION_INTERNET_ALERT) {
    alertEndpoint = `${process.env.REACT_APP_SCENARIO_OUTPUT_URL}/api/handle-internet-contest-alert`
  } else {
    alertEndpoint = `${process.env.REACT_APP_SCENARIO_OUTPUT_URL}/dyn/${getLocale()}/createAlertReact/${
      mapScenarioTypeAndAlertEndpointFragment[scenarioType as Partial<ScenarioType>]
    }`
  }

  const response = await fetch(alertEndpoint, {
    method: 'POST',
    body: JSON.stringify(payloadRawObject),
    // mode: 'no-cors',
    headers: {
      'Content-type': 'application/json',
    },
  })

  if ((await response.json()).success) {
    const locale = getLocale()
    const nav = mapScenarioTypeAndAlertEndpointFragment[scenarioType as Partial<ScenarioType>]
    const typeProviderCookie = nav === 'mobile' ? 'Mobile' : 'Internet'

    let additionalInformation: Record<string, unknown> = {}

    if (getCookie('scenario') && JSON.parse(getCookie('scenario')).initialInformation) {
      additionalInformation = JSON.parse(getCookie('scenario').replace(/%3B/g, ';')).initialInformation
    }

    const searchCriteriaObject = getSearchCriteriaObject(nav, answers, additionalInformation)

    if (nav === 'mobile') {
      sendGA4Event('GA4-alert-create-mobile', {
        event_category: 'alert-create-mobile',
        application_version: 'not set',
        database_build: 'not set',
        region: getCookie('province') || 'not set',
        locale: locale,
        search_criteria: JSON.stringify(searchCriteriaObject) || 'not set',
        current_provider: getCookie(`current${typeProviderCookie}Provider`) || 'not set',
        url: window.location.href,
        source: getCookie('referrerUrl') || document.referrer || window.location.href,
        domain: 'not set',
        email: answers['email-address']![0] || 'not set',
      })
    }

    if (nav === 'internet') {
      sendGA4Event('GA4-alert-create-internet', {
        event_category: 'alert-create-mobile',
        application_version: 'not set',
        database_build: 'not set',
        region: getCookie('province') || 'not set',
        locale: locale,
        search_criteria: JSON.stringify(searchCriteriaObject) || 'not set',
        current_provider: getCookie(`current${typeProviderCookie}Provider`) || 'not set',
        url: window.location.href,
        source: getCookie('referrerUrl') || document.referrer || window.location.href,
        domain: 'not set',
        email: answers['email-address']![0] || 'not set',
        postal_code: answers['postal-code-internet']![0] || 'not set',
      })
    }
  }

  setTimeout(() => {
    identifyCioUser(answers)
  }, 2000)
}

export const getSearchCriteriaObject = (
  nav: string | undefined,
  answers: Answers,
  additionalInformation: Record<string, unknown>,
) => {
  const mapSearchCriteria = mapKeySearchCriterias()

  if (nav === 'mobile') {
    return {
      [mapSearchCriteria.KEY_SEARCH_PHONE_MODEL]: answers['phone-model-id'] ? answers['phone-model-id'][0] : 'not set',
      [mapSearchCriteria.KEY_SEARCH_DATA_AMOUNT]: answers['how-much-data'] ? answers['how-much-data'][0] : 'not set',
      [mapSearchCriteria.KEY_SEARCH_VOICE]: answers['how-many-calling-minutes']
        ? answers['how-many-calling-minutes'][0]
        : 'not set',
      [mapSearchCriteria.KEY_SEARCH_OTHER_FILTERS]: additionalInformation['services'] || 'not set',
    }
  } else if (nav === 'internet') {
    return {
      [mapSearchCriteria.KEY_SEARCH_DOWNLOAD_SPEED]: answers['internet-speed']
        ? answers['internet-speed'][0]
        : 'not set',
      [mapSearchCriteria.KEY_SEARCH_MONTHLY_USAGE]: answers['monthly-usage'] || 0,
      [mapSearchCriteria.KEY_SEARCH_OTHER_FILTERS]: answers['options-alert'] ? answers['options-alert'][0] : 'not set',
    }
  }

  return null
}

export const sendLeadRequest = async (answers: Answers, scenarioType: Partial<ScenarioType>) => {
  // const payload = new FormData()
  const payloadRawObject = createLeadBodyRequest(answers, scenarioType)
  // for (const i in payloadRawObject) {
  //   payload.append(i, payloadRawObject[i] as string)
  // }

  // const endpoint = `${process.env.REACT_APP_SCENARIO_OUTPUT_URL}/dyn/${getLocale()}/createAlert/${
  //   mapScenarioTypeAndAlertEndpointFragment[scenarioType as Partial<ScenarioType>]
  // }`
  // const locale = getLocale()
  const endpoint = `${process.env.REACT_APP_SCENARIO_OUTPUT_URL}/lead`

  const response = await fetch(endpoint, {
    method: 'POST',
    // body: payload,
    body: JSON.stringify(payloadRawObject),
    // mode: 'no-cors',
    headers: {
      'Content-type': 'application/json',
    },
  })

  if ((await response.json()).success) {
    const planId = (payloadRawObject['planId'] as string | undefined) || 'not set'
    const nav = payloadRawObject['nav'] as string
    const locale = getLocale()
    const lineItemId = (payloadRawObject['lineItemId'] as string | undefined) || 'not set'

    const planInfo = () => {
      if (nav === 'mobile') {
        return {
          placeId: (payloadRawObject['placeId'] as string | undefined) || 'not set',
          planId: planId || 'not set',
          planMonthlyPrice: (payloadRawObject['planMonthlyPrice'] as string | undefined) || 'not set',
          planName: (payloadRawObject['planName'] as string | undefined) || 'not set',
          planUpfrontPrice: (payloadRawObject['planUpfrontPrice'] as string | undefined) || 'not set',
        }
      }

      if (nav === 'internet') {
        return {
          placeId: (payloadRawObject['placeId'] as string | undefined) || 'not set',
          planDataUsage: (payloadRawObject['planDataUsage'] as string | undefined) || 'not set',
          planId: planId || 'not set',
          planMonthlyPrice: (payloadRawObject['planMonthlyPrice'] as string | undefined) || 'not set',
          planName: (payloadRawObject['planName'] as string | undefined) || 'not set',
          planUpfrontPrice: (payloadRawObject['planUpfrontPrice'] as string | undefined) || 'not set',
        }
      }
    }

    // const cplType = nav === 'internet' ? 'internetTS' : 'mobileTS'
    // console.log('cplType: ' + cplType)
    // const cpl = (payloadRawObject['internetTS'] as number) || 'not set'
    const providerName = (payloadRawObject['providerName'] as string) || 'not set'

    sendGA4Event('purchase', {
      currency: 'CAD',
      transation_id: Date.now(),
      value: payloadRawObject['tl'] || 'not set', //(payloadRawObject[cplType] as number) || 'not set',
      items: [
        {
          item_id: planId,
          locale: locale,
          item_category: payloadRawObject['nav'],
          item_category2: 'nativeCTA',
          item_category3: getCookie('pageRoute') || 'question',
          item_category4: 'cpl',
          item_category5: payloadRawObject['tl'] || 'not set',
          item_brand: providerName,
          price: payloadRawObject['tl'] || 'not set', // (payloadRawObject[cplType] as number) || 'not set',
          planInfo: planInfo(), // on verra si ca fonctionne
          affiliation: 'questionnaire',
          line_item_id: lineItemId,
        },
      ],
    })
  }

  setTimeout(() => {
    identifyCioUser(answers)
  }, 2000)
}

export const checkProviderAvailability = async (providerSlug: string): Promise<Array<string>> => {
  const endpoint = `${process.env.REACT_APP_SCENARIO_OUTPUT_URL}/home-internet/modal/is-provider-available/${providerSlug}?dyn=1`
  const placeId = getCookie('placeId')

  const response = await fetch(endpoint, {
    method: 'POST',
    body: JSON.stringify({ placeId }),
    headers: {
      'Content-type': 'application/json',
    },
  })

  return (await response.json()).deal.toString()
}

export const getEmailFromAnswers = (answers: Answers) => {
  return answers['email-address']
    ? answers['email-address'][0]
    : answers['email-address-lead']
    ? answers['email-address-lead'][0]
    : answers['email-address-optional']
    ? answers['email-address-optional'][0]
    : answers['email-address-newsletter']
    ? answers['email-address-newsletter'][0]
    : answers['email-address-optional-with-alert']
    ? answers['email-address-optional-with-alert'][0]
    : answers['email-address-ticket']
    ? answers['email-address-ticket'][0]
    : null
}
